import { FC } from 'react';
import { Modal, FileInput, Button, Typography } from 'melp-design/components';
import { Box, Stack } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FileImportErrorsModal, JobStatusModal } from 'components/modals';
import { ModalProps, useModalContext } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import {
  EmployeesFilters,
  employeesKeys,
  useExportCompanyOrgStructure,
  useExportOrgStructureEmployees,
  useImportOrgStructure,
} from 'store/employees';
import { FileDownloadOutlined } from '@mui/icons-material';
import { useDownloadFile } from 'store/files';
import { SystemColors } from 'melp-design/style';
import { usePredefinedToasts } from 'utils/Toast';
import orgStructureExample from './org-structure-example.jpg';

interface Values {
  file: File | undefined;
}

interface Props extends ModalProps {
  filters: Partial<EmployeesFilters>;
}

export const ImportOrgStructureModal: FC<Props> = ({ closeModal, filters }) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const queryClient = useQueryClient();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit, watch } = useForm<Values>({
    defaultValues: { file: undefined },
    mode: 'onChange',
  });

  const file = watch('file');

  const { mutate: importOrgStructure, status } = useImportOrgStructure();
  const { mutate: downloadFile } = useDownloadFile();
  const { mutate: exportEmployees, isLoading: isExportingEmployees } =
    useExportOrgStructureEmployees();
  const { mutate: exportOrgStructure, isLoading: isExportingOrgStructure } =
    useExportCompanyOrgStructure();

  const openFileJobModal = ({
    jobId,
    queue,
    title,
    description,
  }: {
    jobId: string;
    queue: string;
    title: string;
    description: string;
  }) => {
    openModal({
      component: JobStatusModal,
      props: {
        jobId,
        queue,
        title,
        description,
        onSuccess: (job, closeJobModal) => {
          const fileId = job.output?.fileId;

          if (!fileId) {
            toast.error(t('errors.genericErrorMessage'));
            closeJobModal();
            return;
          }

          downloadFile(
            { fileId },
            {
              onSettled: () => {
                closeJobModal();
              },
            },
          );
        },
      },
      closable: false,
    });
  };

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (values.file) {
      importOrgStructure(
        { file: values.file },
        {
          onSuccess: ({ data }) => {
            closeModal({ action: 'CLOSE' });
            openModal({
              component: JobStatusModal,
              props: {
                jobId: data.jobId,
                queue: data.queue,
                title: t('employees.org_structure.import.in_progress.title'),
                onSuccess: (_, closeJobModal) => {
                  queryClient.invalidateQueries(employeesKeys.all);
                  closeJobModal();
                  predefinedToasts.success.updated();
                },
                onError: (job, closeJobModal) => {
                  const errors = job.input?.errors;

                  closeJobModal();

                  if (!errors?.length) {
                    toast.error(job.error || t('errors.genericErrorMessage'));
                    return;
                  }

                  openModal({
                    component: FileImportErrorsModal,
                    size: 'lg',
                    props: {
                      title: t('employees.org_structure.actions.import'),
                      onBack: () =>
                        openModal({
                          component: ImportOrgStructureModal,
                          size: 'lg',
                          props: { filters },
                        }),
                      errors: errors.map((error) => ({
                        row: error.row,
                        field: String(error.column),
                        message: error.message,
                      })),
                    },
                  });
                },
              },
              closable: false,
            });
          },
        },
      );
    }
  };

  const steps = [
    {
      title: t('employees.org_structure.import.steps.export.title'),
      description: t('employees.org_structure.import.steps.export.description'),
      content: (
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            label={t('employees.org_structure.actions.export_employees')}
            variant="neutral-outline"
            size="sm"
            icon={FileDownloadOutlined}
            type="button"
            loading={isExportingEmployees}
            onClick={() =>
              exportEmployees(
                { filters },
                {
                  onSuccess: ({ data }) =>
                    openFileJobModal({
                      jobId: data.jobId,
                      queue: data.queue,
                      title: t(
                        'employees.org_structure.actions.export_employees',
                      ),
                      description: t(
                        'employees.org_structure.import.in_progress.description',
                      ),
                    }),
                },
              )
            }
          />
          <Button
            label={t('employees.org_structure.actions.export_structure')}
            variant="neutral-outline"
            size="sm"
            icon={FileDownloadOutlined}
            type="button"
            loading={isExportingOrgStructure}
            onClick={() =>
              exportOrgStructure(undefined, {
                onSuccess: ({ data }) =>
                  openFileJobModal({
                    jobId: data.jobId,
                    queue: data.queue,
                    title: t(
                      'employees.org_structure.actions.export_structure',
                    ),
                    description: t(
                      'employees.org_structure.import.in_progress.description',
                    ),
                  }),
              })
            }
          />
        </Stack>
      ),
    },
    {
      title: t('employees.org_structure.import.steps.update.title'),
      description: t('employees.org_structure.import.steps.update.description'),
      content: (
        <Box
          component="img"
          src={orgStructureExample}
          alt={t('employees.org_structure.import.steps.update.title')}
          sx={{
            display: 'block',
            maxWidth: '100%',
            borderRadius: 1,
            border: `1px solid ${SystemColors.grey[90]}`,
          }}
        />
      ),
    },
    {
      title: t('employees.consents.import.steps.upload.title'),
      description: t('employees.consents.import.steps.upload.description'),
      content: (
        <Controller
          name="file"
          control={control}
          render={({ field: { name, onChange, ref } }) => (
            <FileInput
              name={name}
              onChange={([nextFile]) => {
                onChange(nextFile);
              }}
              accept={['xls', 'xlsx']}
              maxFiles={1}
              required
              ref={ref}
            />
          )}
        />
      ),
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('employees.org_structure.actions.import')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('actions.import'),
            disabled: !file || status === 'loading',
            loading: status === 'loading',
            type: 'submit',
          },
        ]}
      >
        <Stack gap={2}>
          <Timeline
            sx={{
              m: 0,
              p: 0,
              '& .MuiTimelineItem-root:before': {
                flex: 0,
                p: 0,
              },
            }}
          >
            {steps.map((step, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      m: 0,
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: SystemColors.primary.base,
                      boxShadow: 'none',
                    }}
                  >
                    <Typography variant="h4" color="textWhite">
                      {index + 1}
                    </Typography>
                  </TimelineDot>
                  {index < steps.length - 1 ? <TimelineConnector /> : null}
                </TimelineSeparator>
                <TimelineContent sx={{ pr: 0, pb: 3 }}>
                  <Stack gap={1}>
                    <Box>
                      <Typography variant="h3">{step.title}</Typography>
                      <Typography variant="p2" color="textSecondary">
                        {step.description}
                      </Typography>
                    </Box>
                    {step.content}
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Stack>
      </Modal.Content>
    </form>
  );
};
